
import {
  defineComponent, PropType, ref, computed, watch,
} from 'vue';
import draggable from 'vuedraggable';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import ProgressSpinner from 'primevue/progressspinner';
import FileManager from '@/components/file-manager/FileManager.vue';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
  File,
} from '@/types';
import { useI18n } from 'vue-i18n';
import { UserState } from '@/composables/User';
import { createNewFileInMedia } from '@/composables/configs/configuration';
import { getSocialNetworkIconStyleV4 } from '@/components/template-builder/getters';

interface SocialNetworkItem {
  key: string;
  network: string;
  url: string;
  customIcon?: string;
  // Propriétés spécifiques au séparateur (espacement uniquement)
  separatorPadding?: string;
  separatorPaddingValue?: number;
}

const NETWORK_LIST = [
  { label: 'Separator', value: 'separator', translationKey: 'templateBuilder.fields.separator', isSeparator: true },
  { label: 'Custom', value: 'custom', translationKey: 'templateBuilder.fields.custom' },
  { label: 'Facebook', value: 'facebook', translationKey: 'templateBuilder.fields.facebook' },
  { label: 'X', value: 'twitter', translationKey: 'templateBuilder.fields.twitter' },
  { label: 'YouTube', value: 'youtube', translationKey: 'templateBuilder.fields.youtube' },
  { label: 'Pinterest', value: 'pinterest', translationKey: 'templateBuilder.fields.pinterest' },
  { label: 'Tumblr', value: 'tumblr', translationKey: 'templateBuilder.fields.tumblr' },
  { label: 'Instagram', value: 'instagram', translationKey: 'templateBuilder.fields.instagram' },
  { label: 'WhatsApp', value: 'whatsapp', translationKey: 'templateBuilder.fields.whatsapp' },
  { label: 'Snapchat', value: 'snapchat', translationKey: 'templateBuilder.fields.snapchat' },
  { label: 'TikTok', value: 'tiktok', translationKey: 'templateBuilder.fields.tiktok' },
  { label: 'LinkedIn', value: 'linkedin', translationKey: 'templateBuilder.fields.linkedin' },
  { label: 'Telegram', value: 'telegram', translationKey: 'templateBuilder.fields.telegram' },
  { label: 'Messenger', value: 'messenger', translationKey: 'templateBuilder.fields.messenger' },
  { label: 'Discord', value: 'discord', translationKey: 'templateBuilder.fields.discord' },
  { label: 'Reddit', value: 'reddit', translationKey: 'templateBuilder.fields.reddit' },
  { label: 'Mail', value: 'email', translationKey: 'templateBuilder.fields.email' },
];

export default defineComponent({
  name: 'SocialNetworkInputGroup',

  components: {
    draggable,
    InputText,
    Button,
    Dropdown,
    ProgressSpinner,
    FileManager,
    BaseInputNumber,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },
    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const displayFileManager = ref(false);
    const currentEditIndex = ref<number>(-1);
    const dragOverIndex = ref<number>(-1);
    const iconStyleKey = ref(0); // Clé pour forcer le rafraîchissement des icônes

    // Récupérer le style d'icône actuel depuis le template
    const getIconStyleFromTemplate = (): string => {
      const imgSelector = `${props.parserValues.selector} img`;
      const style = getSocialNetworkIconStyleV4(imgSelector);
      return style || '1';
    };
    const iconStyle = ref(getIconStyleFromTemplate());
    const uploadingIndex = ref<number>(-1);
    const idShop: number = UserState.activeShop ? UserState.activeShop.id : 0;

    const socialNetworks = ref<SocialNetworkItem[]>([]);

    const initializeSocialNetworks = () => {
      const items = property.value.value || [];
      socialNetworks.value = items.map((item: any) => ({
        key: item.key || `spm_${item.network || 'custom'}_${Date.now()}`,
        network: item.network || extractNetworkFromKey(item.key),
        url: item.value || item.url || '',
        customIcon: item.customIcon || '',
        // Propriétés du séparateur (espacement uniquement)
        separatorPadding: item.separatorPadding || '5px',
        separatorPaddingValue: parseInt((item.separatorPadding || '5px').replace(/\D/g, ''), 10) || 5,
      }));
    };

    const extractNetworkFromKey = (key: string): string => {
      if (!key) return 'custom';
      const match = key.match(/spm_(\w+)/);
      return match ? match[1] : 'custom';
    };

    const availableNetworks = computed(() => NETWORK_LIST.map((network) => ({
      ...network,
      label: t(network.translationKey),
    })));

    const getNetworkLabel = (element: SocialNetworkItem): string => {
      const network = NETWORK_LIST.find((n) => n.value === element.network);
      return network ? t(network.translationKey) : t('templateBuilder.fields.custom');
    };

    const getNetworkDisplayLabel = (networkValue: string): string => {
      const network = NETWORK_LIST.find((n) => n.value === networkValue);
      return network ? t(network.translationKey) : networkValue;
    };

    const getNetworkIconUrl = (network: string): string => {
      if (network === 'custom') return '';
      // Forcer le style 2 pour les dropdowns (icônes carrées avec fond)
      return `https://media.shopimind.io/img/icons/socialv2/2/${network}.png`;
    };

    const generateUniqueKey = (network: string): string => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      return `spm_${network}_${timestamp}_${random}`;
    };

    const emitChanges = () => {
      const formattedValue = socialNetworks.value.map((item) => ({
        key: item.key,
        network: item.network,
        label: `templateBuilder.fields.${item.network}`,
        value: item.url,
        customIcon: item.customIcon,
        // Propriétés du séparateur (espacement uniquement)
        separatorPadding: item.separatorPadding,
      }));

      property.value.value = formattedValue;
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const handleAddNetwork = () => {
      const newNetwork: SocialNetworkItem = {
        key: generateUniqueKey('custom'),
        network: 'facebook',
        url: '',
        customIcon: '',
        separatorPadding: '5px',
        separatorPaddingValue: 5,
      };
      socialNetworks.value.push(newNetwork);
      emitChanges();
    };

    const handleRemoveNetwork = (index: number) => {
      socialNetworks.value.splice(index, 1);
      emitChanges();
    };

    const handleNetworkTypeChange = (index: number, event: any) => {
      const newNetwork = event.value;
      socialNetworks.value[index].network = newNetwork;
      socialNetworks.value[index].key = generateUniqueKey(newNetwork);
      if (newNetwork !== 'custom') {
        socialNetworks.value[index].customIcon = '';
      }
      emitChanges();
    };

    const handleUrlChange = (index: number) => {
      emitChanges();
    };

    const handleOrderChange = () => {
      emitChanges();
    };

    // Fonctions pour le séparateur
    const handleSeparatorPaddingInput = (index: number, value: number) => {
      socialNetworks.value[index].separatorPaddingValue = value;
      socialNetworks.value[index].separatorPadding = `${value}px`;
      emitChanges();
    };

    const openFileManager = (index: number) => {
      currentEditIndex.value = index;
      displayFileManager.value = true;
    };

    const onChooseCustomIcon = (file: File) => {
      if (currentEditIndex.value >= 0 && currentEditIndex.value < socialNetworks.value.length) {
        socialNetworks.value[currentEditIndex.value].customIcon = file.url;
        emitChanges();
      }
      displayFileManager.value = false;
      currentEditIndex.value = -1;
    };

    const handleRemoveCustomIcon = (index: number) => {
      socialNetworks.value[index].customIcon = '';
      emitChanges();
    };

    const onDragOver = (event: DragEvent, index: number) => {
      dragOverIndex.value = index;
    };

    const onDragLeave = () => {
      dragOverIndex.value = -1;
    };

    const convertFileToBase64 = (file: globalThis.File): Promise<string> => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        resolve(e.target.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });

    const uploadFile = async (file: globalThis.File, index: number) => {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        return;
      }

      uploadingIndex.value = index;
      try {
        const base64Data = await convertFileToBase64(file);
        const uploadResult = await createNewFileInMedia([base64Data], idShop, '');

        if (uploadResult?.data && Array.isArray(uploadResult.data) && uploadResult.data.length > 0) {
          const uploadedFile = uploadResult.data[0];
          const imageUrl = `https://media.shopimind.io/clients/${uploadedFile.path}`;
          socialNetworks.value[index].customIcon = imageUrl;
          emitChanges();
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        uploadingIndex.value = -1;
      }
    };

    const onDrop = (event: DragEvent, index: number) => {
      dragOverIndex.value = -1;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        uploadFile(file, index);
      }
    };

    watch(() => props.parserValues.properties[0], () => {
      property.value = props.parserValues.properties[0];
      initializeSocialNetworks();
      // Rafraîchir le style d'icône depuis le template
      iconStyle.value = getIconStyleFromTemplate();
    }, { immediate: true, deep: true });

    return {
      t,
      socialNetworks,
      availableNetworks,
      displayFileManager,
      getNetworkLabel,
      getNetworkDisplayLabel,
      getNetworkIconUrl,
      handleAddNetwork,
      handleRemoveNetwork,
      handleNetworkTypeChange,
      handleUrlChange,
      handleOrderChange,
      openFileManager,
      onChooseCustomIcon,
      handleRemoveCustomIcon,
      onDragOver,
      onDragLeave,
      onDrop,
      dragOverIndex,
      uploadingIndex,
      iconStyleKey,
      // Séparateur
      handleSeparatorPaddingInput,
    };
  },
});
